import { FileText, FlaskConical, ImageIcon, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { useExamesControllerListar } from '@/api/generated/exames/exames';
import { ApiError } from '@/api/http-client';
import { ExameExternoDialogo } from '@/features/exames/components/ExameExternoDialogo';
// Data de coleta é DATA (meia-noite UTC), não instante — lida em UTC, senão no Brasil cai no dia anterior.
import { formatarData } from '@/features/exames/rotulos';
import {
  type ArquivoSelecionado,
  type ExameSelecionado,
  type ItensSelecionados,
  type OperacoesNoServidor,
} from '@/features/historico/itensDoProntuario';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

const FORMATOS = 'application/pdf,image/png,image/jpeg,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_ITENS = 20;

const ROTULO_ORIGEM: Record<string, string> = { MANUAL: 'Interno', VERTIS: 'Vertis', EXTERNO: 'Externo' };

/** Título padrão = nome do arquivo sem a extensão; a pessoa troca por algo que diga o que é. */
function tituloPadrao(nome: string): string {
  return nome.replace(/\.[^.]+$/, '').slice(0, 120) || 'Arquivo';
}

const mensagemDeErro = (erro: unknown, padrao: string) => (erro instanceof ApiError ? erro.message : padrao);

function formatarTamanho(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface Props {
  pacienteId: string;
  valor: ItensSelecionados;
  aoMudar: (valor: ItensSelecionados) => void;
  /**
   * Com `servidor` (rascunho), cada ação grava na hora: o arquivo sobe ao ser
   * escolhido, o título salva ao sair do campo, remover apaga. Sem ele
   * (anexo), tudo fica na tela e sobe junto no "Salvar anexo".
   */
  servidor?: OperacoesNoServidor;
  /** Quantos envios estão em andamento — o pai não deixa salvar com arquivo subindo. */
  aoMudarEnviando: (enviando: number) => void;
  /** Exames que já estão neste prontuário (cadastro ou outro anexo) — não aparecem para escolher de novo. */
  examesJaVinculados?: string[];
}

/**
 * "Arquivos e exames" (item 16) — o mesmo bloco no rascunho do prontuário e no
 * anexo: fotos/PDFs com título próprio, exames do paciente e o atalho para
 * cadastrar um exame externo sem sair do formulário.
 *
 * Depois de registrado nada disso se remove — por isso o ✕ só existe aqui.
 */
export function ArquivosEExames({ pacienteId, valor, aoMudar, servidor, aoMudarEnviando, examesJaVinculados = [] }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState<string[]>([]);
  const [escolhendoExames, setEscolhendoExames] = useState(false);
  const [cadastrandoExame, setCadastrandoExame] = useState(false);
  // O pai recebe as mudanças pela prop; `valorRef` evita que envios paralelos
  // se atropelem usando um `valor` velho da closure.
  const valorRef = useRef(valor);
  valorRef.current = valor;
  /** O último título que o servidor tem de cada arquivo — só renomeia o que mudou de fato. */
  const titulosSalvos = useRef(new Map<string, string>());

  const exames = useExamesControllerListar(
    { pacienteId, porPagina: 50, ordenarPor: 'coletadoEm', ordem: 'desc' },
    { query: { enabled: escolhendoExames } },
  );

  const totalDeItens = valor.arquivos.length + valor.exames.length;
  const idsEscolhidos = new Set([...valor.exames.map((e) => e.exameId), ...examesJaVinculados]);

  function mudar(parcial: Partial<ItensSelecionados>) {
    aoMudar({ ...valorRef.current, ...parcial });
  }

  function marcarEnviando(nome: string, entrando: boolean) {
    setEnviando((lista) => {
      let nova = lista;
      if (entrando) nova = [...lista, nome];
      else {
        const indice = lista.indexOf(nome);
        if (indice !== -1) nova = [...lista.slice(0, indice), ...lista.slice(indice + 1)];
      }
      aoMudarEnviando(nova.length);
      return nova;
    });
  }

  async function adicionar(arquivos: File[]) {
    const validos = arquivos.filter((arquivo) => {
      if (arquivo.size > MAX_BYTES) {
        toast.error(`${arquivo.name}: acima de 10 MB.`);
        return false;
      }
      return true;
    });
    if (totalDeItens + validos.length > MAX_ITENS) {
      toast.error(`No máximo ${MAX_ITENS} arquivos e exames.`);
      return;
    }

    if (!servidor) {
      mudar({
        arquivos: [
          ...valorRef.current.arquivos,
          ...validos.map((arquivo) => ({
            chave: crypto.randomUUID(),
            titulo: tituloPadrao(arquivo.name),
            nomeOriginal: arquivo.name,
            mimeType: arquivo.type,
            tamanhoBytes: arquivo.size,
            arquivo,
          })),
        ],
      });
      return;
    }

    await Promise.all(
      validos.map(async (arquivo) => {
        marcarEnviando(arquivo.name, true);
        try {
          const enviado = await servidor.enviarArquivo(arquivo, tituloPadrao(arquivo.name));
          titulosSalvos.current.set(enviado.chave, enviado.titulo);
          mudar({ arquivos: [...valorRef.current.arquivos, enviado] });
        } catch (erro) {
          toast.error(`${arquivo.name}: ${mensagemDeErro(erro, 'não foi possível enviar.')}`);
        } finally {
          marcarEnviando(arquivo.name, false);
        }
      }),
    );
  }

  function mudarTitulo(item: ArquivoSelecionado, titulo: string) {
    mudar({ arquivos: valorRef.current.arquivos.map((a) => (a.chave === item.chave ? { ...a, titulo } : a)) });
  }

  /** Rascunho: o título vai ao servidor ao sair do campo — vazio não vai (o registrar trava antes). */
  function confirmarTitulo(item: ArquivoSelecionado) {
    if (!servidor) return;
    const titulo = item.titulo.trim();
    if (!titulo || titulosSalvos.current.get(item.chave) === titulo) return;
    titulosSalvos.current.set(item.chave, titulo);
    servidor.renomearArquivo(item.chave, titulo).catch((erro: unknown) => {
      titulosSalvos.current.delete(item.chave);
      toast.error(mensagemDeErro(erro, 'Não foi possível salvar o título.'));
    });
  }

  async function remover(item: ArquivoSelecionado) {
    if (servidor) {
      try {
        await servidor.removerArquivo(item.chave);
      } catch (erro) {
        toast.error(mensagemDeErro(erro, 'Não foi possível remover o arquivo.'));
        return;
      }
    }
    mudar({ arquivos: valorRef.current.arquivos.filter((a) => a.chave !== item.chave) });
  }

  async function alternarExame(exame: ExameSelecionado, marcado: boolean) {
    if (servidor) {
      try {
        if (marcado) await servidor.vincularExame(exame.exameId);
        else await servidor.desvincularExame(exame.exameId);
      } catch (erro) {
        toast.error(mensagemDeErro(erro, 'Não foi possível atualizar os exames.'));
        return;
      }
    }
    const atuais = valorRef.current.exames;
    mudar({
      exames: marcado ? [...atuais.filter((e) => e.exameId !== exame.exameId), exame] : atuais.filter((e) => e.exameId !== exame.exameId),
    });
  }

  return (
    <div className="space-y-2" role="group" aria-label="Arquivos e exames">
      <p className="text-sm font-medium text-brand">Arquivos e exames</p>

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={FORMATOS}
          className="hidden"
          aria-label="Escolher fotos ou PDFs"
          onChange={(e) => {
            void adicionar(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          <Paperclip className="w-3.5 h-3.5" /> Anexar foto/PDF
        </Button>
        <Button
          type="button"
          variant={escolhendoExames ? 'default' : 'outline'}
          size="sm"
          aria-pressed={escolhendoExames}
          onClick={() => setEscolhendoExames((v) => !v)}
        >
          <FlaskConical className="w-3.5 h-3.5" /> Vincular exame
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setCadastrandoExame(true)}>
          <Plus className="w-3.5 h-3.5" /> Cadastrar exame externo
        </Button>
      </div>

      {escolhendoExames && (
        <div className="max-h-48 overflow-y-auto rounded-md border border-brand/15 p-2 space-y-1" aria-label="Exames do paciente">
          {exames.isPending ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando exames...
            </p>
          ) : (exames.data?.itens ?? []).filter((e) => !examesJaVinculados.includes(e.id)).length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum exame deste paciente para vincular.</p>
          ) : (
            (exames.data?.itens ?? [])
              .filter((e) => !examesJaVinculados.includes(e.id))
              .map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={idsEscolhidos.has(e.id)}
                    onCheckedChange={(v) =>
                      void alternarExame(
                        { exameId: e.id, nome: e.exameNome, coletadoEm: e.coletadoEm, origem: e.origem },
                        v === true,
                      )
                    }
                    aria-label={`Vincular ${e.exameNome}`}
                  />
                  <span className="flex-1 truncate">{e.exameNome}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatarData(e.coletadoEm)} · {ROTULO_ORIGEM[e.origem] ?? e.origem}
                  </span>
                </label>
              ))
          )}
        </div>
      )}

      {(enviando.length > 0 || totalDeItens > 0) && (
        <ul className="space-y-1.5">
          {enviando.map((nome, indice) => (
            <li key={`enviando-${indice}`} className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando {nome}...
            </li>
          ))}
          {valor.arquivos.map((arquivo) => (
            <li key={arquivo.chave} className="flex items-center gap-2">
              {arquivo.mimeType === 'application/pdf' ? (
                <FileText className="w-4 h-4 shrink-0 text-brand" />
              ) : (
                <ImageIcon className="w-4 h-4 shrink-0 text-brand" />
              )}
              <Input
                value={arquivo.titulo}
                maxLength={120}
                aria-label={`Título de ${arquivo.nomeOriginal}`}
                aria-invalid={!arquivo.titulo.trim()}
                placeholder="Título do arquivo"
                className={`${CLASSE_CONTROLE} h-8 flex-1`}
                onChange={(e) => mudarTitulo(arquivo, e.target.value)}
                onBlur={() => confirmarTitulo(arquivo)}
              />
              <span className="hidden sm:inline w-40 truncate text-xs text-muted-foreground" title={arquivo.nomeOriginal}>
                {arquivo.nomeOriginal} · {formatarTamanho(arquivo.tamanhoBytes)}
              </span>
              <button
                type="button"
                aria-label={`Remover ${arquivo.nomeOriginal}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => void remover(arquivo)}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
          {valor.exames.map((exame) => (
            <li key={exame.exameId} className="flex items-center gap-2 text-sm">
              <FlaskConical className="w-4 h-4 shrink-0 text-brand" />
              <span className="flex-1 truncate">{exame.nome}</span>
              <span className="text-xs text-muted-foreground">
                {formatarData(exame.coletadoEm)} · {ROTULO_ORIGEM[exame.origem] ?? exame.origem}
              </span>
              <button
                type="button"
                aria-label={`Desvincular ${exame.nome}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => void alternarExame(exame, false)}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ExameExternoDialogo
        aberto={cadastrandoExame}
        aoFechar={() => setCadastrandoExame(false)}
        pacienteId={pacienteId}
        aoCadastrar={(exame) =>
          void alternarExame(
            { exameId: exame.id, nome: exame.exameNome, coletadoEm: exame.coletadoEm, origem: exame.origem },
            true,
          )
        }
      />
    </div>
  );
}
